import React, { useState } from 'react';
import { BookOpen, Play, CheckCircle, ArrowRight, X } from 'lucide-react';

const OnboardingGuide = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    {
      title: "Welcome to DeckGen",
      description: "Your AI-powered platform for creating presentations, managing agents, and analyzing performance.",
      icon: <BookOpen className="w-8 h-8 text-blue-600" />,
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">What you can do:</h3>
          <ul className="space-y-2 text-gray-600">
            <li className="flex items-center">
              <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
              Create PowerPoint presentations from text
            </li>
            <li className="flex items-center">
              <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
              Register and manage AI agents
            </li>
            <li className="flex items-center">
              <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
              Track agent performance with analytics
            </li>
            <li className="flex items-center">
              <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
              Set up webhooks for real-time notifications
            </li>
          </ul>
        </div>
      )
    },
    {
      title: "Getting Started",
      description: "Let's set up your first AI agent and start creating content.",
      icon: <Play className="w-8 h-8 text-green-600" />,
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">Quick Start Guide:</h4>
            <ol className="space-y-2 text-blue-700">
              <li>1. Navigate to "Agentic AI Tools" → "Register Agent"</li>
              <li>2. Choose an agent type (customer support, sales, etc.)</li>
              <li>3. Select your preferred LLM (Claude, GPT, etc.)</li>
              <li>4. Submit feedback to track performance</li>
              <li>5. Monitor analytics and enable self-healing</li>
            </ol>
          </div>
        </div>
      )
    },
    {
      title: "Key Features",
      description: "Discover the powerful tools available in your dashboard.",
      icon: <CheckCircle className="w-8 h-8 text-purple-600" />,
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Content Creation</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• PowerPoint presentations</li>
                <li>• FAQ generators</li>
                <li>• Battle cards</li>
                <li>• Architecture diagrams</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Agent Management</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Register AI agents</li>
                <li>• Monitor performance</li>
                <li>• Self-healing capabilities</li>
                <li>• Analytics dashboard</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center space-x-3">
              {steps[currentStep].icon}
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {steps[currentStep].title}
                </h2>
                <p className="text-gray-600">{steps[currentStep].description}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="mb-6">
            {steps[currentStep].content}
          </div>

          <div className="flex justify-between items-center">
            <div className="flex space-x-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full ${
                    index === currentStep ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Skip
              </button>
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <span>{currentStep === steps.length - 1 ? 'Get Started' : 'Next'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingGuide; 